import { useState, useEffect } from 'react';
import axios from 'axios';
import './ContentForm.css';
import API_BASE_URL from '../config';

const ContentForm = ({ onClose, onContentCreated, editContent = null }) => {
  const [formData, setFormData] = useState({
    title: '',
    youtubeUrl: '',
    voiceType: 'female', // 기본값: 여성
    subtitles: [{ english: '', korean: '', comment: '', words: [], startTime: 0, endTime: 0 }]
  });
  const [jsonInput, setJsonInput] = useState('');
  const [inputMode, setInputMode] = useState('json'); // 'json' or 'manual'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // 편집 모드일 때 데이터 로드
  useEffect(() => {
    if (editContent) {
      setIsEditMode(true);
      setFormData({
        title: editContent.title || '',
        youtubeUrl: editContent.youtube_url || '',
        voiceType: editContent.voice_type || 'female',
        subtitles: editContent.subtitles && editContent.subtitles.length > 0
          ? editContent.subtitles
          : [{ english: '', korean: '', comment: '', words: [], startTime: 0, endTime: 0 }]
      });

      // JSON 입력 모드용 데이터 준비
      if (editContent.subtitles && editContent.subtitles.length > 0) {
        const formatTime = (seconds) => {
          const mins = Math.floor(seconds / 60);
          const secs = Math.floor(seconds % 60);
          return `${mins}:${secs.toString().padStart(2, '0')}`;
        };

        const jsonData = {
          title: editContent.title || '',
          url: editContent.youtube_url || '',
          content: editContent.subtitles.map(sub => ({
            eng: sub.english,
            kor: sub.korean,
            time: `${formatTime(sub.startTime)}-${formatTime(sub.endTime)}`,
            comment: sub.comment || '',
            words: sub.words || []
          }))
        };
        setJsonInput(JSON.stringify(jsonData, null, 2));
      }
    }
  }, [editContent]);

  const parseJsonInput = () => {
    try {
      const parsed = JSON.parse(jsonInput);
      
      // 새로운 형식 지원: {title, url, content: [...]}
      let contentArray;
      let title = formData.title;
      let youtubeUrl = formData.youtubeUrl;
      
      if (parsed.title && parsed.content && Array.isArray(parsed.content)) {
        // 새로운 형식
        contentArray = parsed.content;
        title = parsed.title;
        youtubeUrl = parsed.url || formData.youtubeUrl;
      } else if (Array.isArray(parsed)) {
        // 기존 형식 (배열)
        contentArray = parsed;
      } else {
        throw new Error('JSON 데이터는 {title, url, content: [...]} 형식이거나 배열 형태여야 합니다.');
      }

      const subtitles = contentArray.map((item, index) => {
        // 새로운 형식 필드 확인
        const eng = item.eng || item.english;
        const kor = item.kor || item.korean;
        const timeStr = item.time;
        const comment = item.comment || '';
        const words = item.words || [];

        if (!eng || !kor) {
          throw new Error(`${index + 1}번째 항목에 필수 필드가 누락되었습니다. (eng/english, kor/korean)`);
        }

        let startTime = 0;
        let endTime = 0;

        if (timeStr) {
          // 새로운 형식: "mm:ss-mm:ss"
          if (timeStr.includes('-')) {
            const [startStr, endStr] = timeStr.split('-');
            const parseTimeString = (str) => {
              const parts = str.split(':');
              if (parts.length === 2) {
                return parseInt(parts[0]) * 60 + parseInt(parts[1]);
              }
              return 0;
            };
            startTime = parseTimeString(startStr);
            endTime = parseTimeString(endStr);
          } else {
            // 기존 형식: 단일 시간값
            const timeToSeconds = (timeStr) => {
              const parts = timeStr.split(':');
              if (parts.length === 3) {
                return parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
              } else if (parts.length === 2) {
                return parseInt(parts[0]) * 60 + parseInt(parts[1]);
              }
              return parseFloat(timeStr) || 0;
            };
            startTime = timeToSeconds(timeStr);
            endTime = startTime + 3; // 기본 3초 길이
          }
        } else if (item.startTime !== undefined && item.endTime !== undefined) {
          // 기존 형식의 startTime, endTime 사용
          startTime = parseFloat(item.startTime) || 0;
          endTime = parseFloat(item.endTime) || 0;
        }

        return {
          english: eng,
          korean: kor,
          comment: comment,
          words: Array.isArray(words) ? words : [],
          startTime: startTime,
          endTime: endTime
        };
      });

      // 시간이 설정되지 않은 경우 순차적으로 설정
      for (let i = 0; i < subtitles.length; i++) {
        if (subtitles[i].endTime === 0 || subtitles[i].endTime <= subtitles[i].startTime) {
          if (i < subtitles.length - 1) {
            subtitles[i].endTime = subtitles[i + 1].startTime || (subtitles[i].startTime + 3);
          } else {
            subtitles[i].endTime = subtitles[i].startTime + 3;
          }
        }
      }

      setFormData({
        ...formData,
        title: title,
        youtubeUrl: youtubeUrl,
        subtitles: subtitles
      });
      setError('');
    } catch (err) {
      setError(`JSON 파싱 오류: ${err.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(null);

    try {
      const token = localStorage.getItem('adminToken');
      const url = isEditMode
        ? `${API_BASE_URL}/api/admin/contents/${editContent.idx}`
        : `${API_BASE_URL}/api/admin/contents`;
      const method = isEditMode ? 'put' : 'post';

      const response = await axios[method](url, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess({
        message: response.data.message,
        viewerUrl: response.data.viewerUrl,
        contentId: response.data.contentId || editContent?.idx,
        accessKey: response.data.accessKey || editContent?.access_key
      });

      // 3초 후 자동으로 닫기
      setTimeout(() => {
        onContentCreated();
      }, 3000);
    } catch (error) {
      setError(error.response?.data?.error || `컨텐츠 ${isEditMode ? '수정' : '생성'}에 실패했습니다.`);
    } finally {
      setLoading(false);
    }
  };

  const addSubtitle = () => {
    setFormData({
      ...formData,
      subtitles: [...formData.subtitles, { english: '', korean: '', comment: '', words: [], startTime: 0, endTime: 0 }]
    });
  };

  const removeSubtitle = (index) => {
    const newSubtitles = formData.subtitles.filter((_, i) => i !== index);
    setFormData({ ...formData, subtitles: newSubtitles });
  };

  const updateSubtitle = (index, field, value) => {
    const newSubtitles = [...formData.subtitles];
    if (field === 'words') {
      // words 필드는 줄바꿈으로 구분하여 객체 배열로 변환
      // 형식: "word: meaning" 또는 "word:meaning"
      const wordsArray = value.split('\n')
        .filter(line => line.trim() !== '')
        .map(line => {
          const parts = line.split(':');
          if (parts.length >= 2) {
            return {
              word: parts[0].trim(),
              meaning: parts.slice(1).join(':').trim()
            };
          }
          return null;
        })
        .filter(item => item !== null);
      newSubtitles[index][field] = wordsArray;
    } else {
      newSubtitles[index][field] = field.includes('Time') ? parseFloat(value) || 0 : value;
    }
    setFormData({ ...formData, subtitles: newSubtitles });
  };

  return (
    <div className="content-form-overlay">
      <div className="content-form">
        <div className="form-header">
          <h2>{isEditMode ? '컨텐츠 수정' : '새 컨텐츠 추가'}</h2>
          <button onClick={onClose} className="close-btn">&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>제목</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>YouTube URL</label>
            <input
              type="url"
              value={formData.youtubeUrl}
              onChange={(e) => setFormData({ ...formData, youtubeUrl: e.target.value })}
              placeholder="https://www.youtube.com/watch?v=..."
              required
            />
          </div>

          <div className="form-group">
            <label>목소리 선택</label>
            <div className="voice-selection">
              <label className="radio-option">
                <input
                  type="radio"
                  name="voiceType"
                  value="female"
                  checked={formData.voiceType === 'female'}
                  onChange={(e) => setFormData({ ...formData, voiceType: e.target.value })}
                />
                <span>여성</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="voiceType"
                  value="male"
                  checked={formData.voiceType === 'male'}
                  onChange={(e) => setFormData({ ...formData, voiceType: e.target.value })}
                />
                <span>남성</span>
              </label>
            </div>
          </div>

          <div className="subtitles-section">
            <div className="subtitles-header">
              <h3>자막 데이터</h3>
              <div className="input-mode-toggle">
                <button
                  type="button"
                  className={inputMode === 'json' ? 'active' : ''}
                  onClick={() => setInputMode('json')}
                >
                  JSON 입력
                </button>
                <button
                  type="button"
                  className={inputMode === 'manual' ? 'active' : ''}
                  onClick={() => setInputMode('manual')}
                >
                  수동 입력
                </button>
              </div>
            </div>

            {inputMode === 'json' ? (
              <div className="json-input-section">
                <div className="form-group">
                  <label>JSON 데이터 입력</label>
                  <textarea
                    value={jsonInput}
                    onChange={(e) => setJsonInput(e.target.value)}
                    placeholder={`{"title": "세계 1위 부자가 말하는 자신이 모든 걸 잃고 할 사업","url": "https://www.youtube.com/shorts/dHlPAogEhlU","content": [{"eng": "If you took Tesla out of it, took Solar City out of it, took SpaceX out of it,","kor": "만약 당신이 테슬라를 제외하고, 솔라시티를 제외하고, 스페이스X를 제외한 상황에서,","time": "0:00-0:05","comment": "'took A out of it' 패턴이 반복됩니다. 특정 요소들을 배제하는 가정을 설정할 때 유용합니다.","words": [{"word": "take out","meaning": "제외하다, 꺼내다"}]},{"eng": "and you said 'I could go start a new company tomorrow,' it would be what?","kor": "내일 당장 새로운 회사를 시작할 수 있다고 한다면, 무엇을 하시겠습니까?","time": "0:05-0:18","comment": "'It would be what?'은 상대방의 선택이나 정체를 물을 때 쓰는 구어체 표현입니다.","words": [{"word": "tomorrow","meaning": "내일"},{"word": "area","meaning": "분야, 영역"}]}]}`}
                    rows="10"
                    className="json-textarea"
                  />
                </div>
                <button
                  type="button"
                  onClick={parseJsonInput}
                  className="btn-parse"
                >
                  JSON 파싱하기
                </button>
                {formData.subtitles.length > 1 && (
                  <div className="parsed-info">
                    ✓ {formData.subtitles.length}개의 자막이 파싱되었습니다.
                  </div>
                )}
              </div>
            ) : (
              <div className="manual-input-section">
                <button type="button" onClick={addSubtitle} className="btn-add">
                  자막 추가
                </button>

                {formData.subtitles.map((subtitle, index) => (
                  <div key={index} className="subtitle-item">
                    <div className="subtitle-header">
                      <span>자막 #{index + 1}</span>
                      {formData.subtitles.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSubtitle(index)}
                          className="btn-remove"
                        >
                          삭제
                        </button>
                      )}
                    </div>

                    <div className="subtitle-fields">
                      <div className="form-group">
                        <label>영어</label>
                        <textarea
                          value={subtitle.english}
                          onChange={(e) => updateSubtitle(index, 'english', e.target.value)}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>한국어</label>
                        <textarea
                          value={subtitle.korean}
                          onChange={(e) => updateSubtitle(index, 'korean', e.target.value)}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>해설 (Comment)</label>
                        <textarea
                          value={subtitle.comment || ''}
                          onChange={(e) => updateSubtitle(index, 'comment', e.target.value)}
                          placeholder="문법이나 표현에 대한 해설을 입력하세요"
                          rows="2"
                        />
                      </div>

                      <div className="form-group">
                        <label>단어 (Words)</label>
                        <textarea
                          value={Array.isArray(subtitle.words) ?
                            subtitle.words.map(w => `${w.word}: ${w.meaning}`).join('\n') : ''}
                          onChange={(e) => updateSubtitle(index, 'words', e.target.value)}
                          placeholder="한 줄에 하나씩 '단어: 뜻' 형식으로 입력하세요&#10;besides: ~외에도&#10;sustainable: 지속 가능한&#10;multi-planetary: 다행성의&#10;destiny: 운명"
                          rows="4"
                        />
                      </div>

                      <div className="time-fields">
                        <div className="form-group">
                          <label>시작 시간 (초)</label>
                          <input
                            type="number"
                            step="0.1"
                            value={subtitle.startTime}
                            onChange={(e) => updateSubtitle(index, 'startTime', e.target.value)}
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>종료 시간 (초)</label>
                          <input
                            type="number"
                            step="0.1"
                            value={subtitle.endTime}
                            onChange={(e) => updateSubtitle(index, 'endTime', e.target.value)}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && <div className="error-message">{error}</div>}
          {success && (
            <div className="success-message">
              <h4>✅ {success.message}</h4>
              <div className="success-details">
                <p><strong>컨텐츠 ID:</strong> {success.contentId}</p>
                <p><strong>뷰어 URL:</strong></p>
                <div className="url-display">
                  <code>{window.location.origin}{success.viewerUrl}</code>
                </div>
                <p className="auto-close-notice">3초 후 자동으로 닫힙니다...</p>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-cancel">
              취소
            </button>
            <button type="submit" disabled={loading} className="btn-submit">
              {loading ? `${isEditMode ? '수정' : '생성'} 중...` : `컨텐츠 ${isEditMode ? '수정' : '생성'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContentForm;